import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SubTabs, useAdminGet } from './kit';
import { rowsOf, str } from './ipintel/shared';
import type { AdminTargetIn } from './adminNav';
import { AdminLink } from './adminNav';
import HzOverview from './horizon/Overview';
import HzTechnologies from './horizon/Technologies';
import HzResults from './horizon/Results';
import HzRuns from './horizon/Runs';
import HzPrompts from './horizon/Prompts';
import HzPending from './horizon/Pending';
import TechDrawer from './horizon/TechDrawer';
import ResultDrawer from './horizon/ResultDrawer';
import {
  HZ_INDUSTRIES, type HzCtx, type PendingFilters, type Preset, type ResultFilters, S, type TechFilters, type Uni,
  promptList,
} from './horizon/shared';

/**
 * /admin → Horizon data: every Horizon sandbox prompt, run and result, which
 * technologies have Horizon data and which are still pending, and the data
 * the public tech pages show (Backend/admin_horizon.py, section "horizon").
 *
 *  - Overview: coverage (have / pending, by university), the spread by
 *    horizon / industry / value lever, and the source in use — the prompt
 *    priority that decides each technology's current result.
 *  - Technologies: every technology with its status and current data; a row
 *    opens its drawer (current data + every result it ever got).
 *  - Results: all sandbox results, server-paged, every column filterable.
 *  - Runs, Prompts: the sandbox runs and the prompts (full texts, the files
 *    in Backend/horizon/prompts, the source priority editor).
 *  - Pending: technologies without Horizon data, and the run that fills
 *    them from here — estimate, cost cap, live progress, actual cost, the
 *    public pages refreshed after — with a log of every such run
 *    (horizon/RunPending.tsx, Backend/horizon_jobs.py).
 *
 * Deep links (admin/adminNav.tsx): /admin?group=horizon&tab=<overview|
 * technologies|results|runs|prompts|pending> plus tech=<id> (opens its
 * drawer), result=<id>, run=<id>, prompt=<id>, university=<code>,
 * status=<has|pending|all>, horizon, lever, industry, q. The Tech URL pages
 * admin links here, and each technology links back to its page there.
 */

const TABS = ['Overview', 'Technologies', 'Results', 'Runs', 'Prompts', 'Pending'];
const TAB_PARAM: Record<string, string> = {
  overview: 'Overview', technologies: 'Technologies', tech: 'Technologies', techs: 'Technologies',
  results: 'Results', runs: 'Runs', prompts: 'Prompts', source: 'Prompts', pending: 'Pending',
};

const HorizonPanel: React.FC<{ target?: AdminTargetIn; initialTab?: string }> = ({ target, initialTab }) => {
  const start = initialTab && TABS.includes(initialTab) ? initialTab : TABS[0];
  const [tab, setTab] = useState(start);
  // Tabs mount on first visit and then stay (hidden), keeping their filters.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [start]: true });
  const pick = useCallback((t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); }, []);

  const [dataTick, setDataTick] = useState(0);
  const overview = useAdminGet(S, 'overview', {}, [dataTick]);
  const promptsRes = useAdminGet(S, 'prompts', {}, [dataTick]);
  const runsRes = useAdminGet(S, 'runs', {}, [dataTick]);

  const prompts = useMemo(() => promptList(rowsOf(promptsRes.data, 'prompts')), [promptsRes.data]);
  const runs = useMemo(() => rowsOf(runsRes.data, 'runs'), [runsRes.data]);
  const universities = useMemo<Uni[]>(() => rowsOf(overview.data?.coverage_by_university)
    .map((u: any) => ({ code: str(u?.code), label: str(u?.label), name: str(u?.name) }))
    .filter((u: Uni) => u.code), [overview.data]);
  const industries = useMemo(() => {
    const got = rowsOf(overview.data?.by_industry).map((x: any) => str(x?.industry ?? x?.label)).filter(Boolean);
    return Array.from(new Set([...HZ_INDUSTRIES, ...got]));
  }, [overview.data]);

  const [techPreset, setTechPreset] = useState<Preset<TechFilters>>(null);
  const [resultPreset, setResultPreset] = useState<Preset<ResultFilters>>(null);
  const [pendingPreset, setPendingPreset] = useState<Preset<PendingFilters>>(null);
  const [promptFocus, setPromptFocus] = useState<{ id: string; n: number } | null>(null);
  const [openTech, setOpenTech] = useState<string | null>(null);
  const [openResult, setOpenResult] = useState<string | null>(null);

  // Escape closes the topmost drawer only (the result drawer opens over the technology one).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openResult) setOpenResult(null);
      else if (openTech) setOpenTech(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openTech, openResult]);

  const ctx: HzCtx = useMemo(() => ({
    prompts,
    promptById: (id: any) => prompts.find(p => p.id === str(id)),
    runs,
    universities,
    industries,
    dataTick,
    openTech: (id: string) => { if (id) setOpenTech(id); },
    openResult: (id: string) => { if (id) setOpenResult(id); },
    closeDrawers: () => { setOpenResult(null); setOpenTech(null); },
    goTab: (t: string) => { setOpenResult(null); setOpenTech(null); pick(t); },
    goTech: f => { setOpenResult(null); setOpenTech(null); setTechPreset(p => ({ f, n: (p?.n || 0) + 1 })); pick('Technologies'); },
    goResults: f => { setOpenResult(null); setOpenTech(null); setResultPreset(p => ({ f, n: (p?.n || 0) + 1 })); pick('Results'); },
    goPending: f => { setOpenResult(null); setOpenTech(null); setPendingPreset(p => ({ f, n: (p?.n || 0) + 1 })); pick('Pending'); },
    goPrompt: (id: string) => { setOpenResult(null); setOpenTech(null); setPromptFocus(p => ({ id, n: (p?.n || 0) + 1 })); pick('Prompts'); },
    reloadAll: () => setDataTick(t => t + 1),
  }), [prompts, runs, universities, industries, dataTick, pick]);

  // Deep link: a tab, filters for it, and/or a drawer to open.
  useEffect(() => {
    if (!target) return;
    const raw = str(target.tab);
    const t = TABS.includes(raw) ? raw : TAB_PARAM[raw.toLowerCase()];
    const has = (k: string) => !!str(target[k]);
    const pickOut = <T,>(keys: string[]): Partial<T> => {
      const out: Record<string, string> = {};
      keys.forEach(k => { if (has(k)) out[k] = str(target[k]); });
      return out as Partial<T>;
    };
    if (t === 'Results' || (!t && (has('run') || has('error')))) {
      setResultPreset(p => ({ f: pickOut<ResultFilters>(['q', 'prompt', 'run', 'university', 'horizon', 'confidence', 'industry', 'lever', 'tag', 'error', 'reviewed', 'approved', 'from', 'to']), n: (p?.n || 0) + 1 }));
      pick('Results');
    } else if (t === 'Pending') {
      setPendingPreset(p => ({ f: pickOut<PendingFilters>(['q', 'university']), n: (p?.n || 0) + 1 }));
      pick('Pending');
    } else if (t === 'Prompts') {
      if (has('prompt')) setPromptFocus(p => ({ id: str(target.prompt), n: (p?.n || 0) + 1 }));
      pick('Prompts');
    } else if (t === 'Technologies' || (!t && ['q', 'university', 'status', 'horizon', 'lever', 'industry', 'confidence', 'tag', 'prompt'].some(has))) {
      const f = pickOut<TechFilters>(['q', 'university', 'industry', 'horizon', 'confidence', 'lever', 'tag', 'status', 'prompt']);
      if (Object.keys(f).length || !techPreset) setTechPreset(p => ({ f, n: (p?.n || 0) + 1 }));
      pick('Technologies');
    } else if (t) pick(t);
    else if (has('tech')) pick('Technologies');
    if (has('tech')) setOpenTech(str(target.tech));
    if (has('result')) setOpenResult(str(target.result));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const pane = (t: string, el: React.ReactNode) =>
    seen[t] ? <div key={t} className={tab === t ? 'flex flex-col gap-4' : 'hidden'}>{el}</div> : null;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={pick} />
        <span className="flex items-center gap-3 flex-wrap text-xs">
          <AdminLink to={{ group: 'tech_pages', tab: 'tech' }}>Tech URL pages →</AdminLink>
          <AdminLink to={{ group: 'labs' }} title="The Horizon Sandbox experiment tools (run a prompt on a sample, review results)">
            AI Labs sandbox →
          </AdminLink>
        </span>
      </div>
      {pane('Overview', <HzOverview res={overview} ctx={ctx} />)}
      {pane('Technologies', <HzTechnologies ctx={ctx} preset={techPreset} />)}
      {pane('Results', <HzResults ctx={ctx} preset={resultPreset} />)}
      {pane('Runs', <HzRuns ctx={ctx} res={runsRes} />)}
      {pane('Prompts', <HzPrompts ctx={ctx} res={promptsRes} source={overview.data?.source} focus={promptFocus} />)}
      {pane('Pending', <HzPending ctx={ctx} preset={pendingPreset} />)}
      {openTech && <TechDrawer id={openTech} ctx={ctx} onClose={() => setOpenTech(null)} />}
      {openResult && <ResultDrawer id={openResult} ctx={ctx} onClose={() => setOpenResult(null)} />}
    </div>
  );
};

export default HorizonPanel;
